import React, { FocusEvent, memo, useCallback } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TextField, TextFieldProps, Typography } from '@mui/material';
import { ContainerCard } from '@provider-portal/common/components/ContainerCard/ContainerCard';

import { DAY_LAST_MINUTE } from './constants';
import { ScheduleDayType } from './types';
import { convertMinutesToTimeString, convertTimeStringToMinutes } from './utils';

interface TimePickerProps extends Pick<TextFieldProps, 'label' | 'helperText'> {
  index: number;
  value: number;
  onChange: (index: number, value: number) => void;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, index, ...textFieldProps }) => {
  const onChangeCallback = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      onChange(index, convertTimeStringToMinutes(e.target.value));
    },
    [index, onChange],
  );

  return (
    <TextField
      type="time"
      defaultValue={convertMinutesToTimeString(value)}
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
      inputProps={{ step: 300 }}
      onBlur={onChangeCallback}
      {...textFieldProps}
    />
  );
};
function updateScheduleDay(
  day: ScheduleDayType,
  start?: number,
  stop?: number,
  intervalIndex?: number,
): ScheduleDayType {
  const hasNoValues = start === undefined && stop === undefined;

  if (intervalIndex !== undefined) {
    const newValue = JSON.parse(JSON.stringify(day));

    if (hasNoValues) {
      // Delete
      newValue.splice(intervalIndex, 1);
    } else {
      // Change start
      if (start !== undefined) {
        newValue[intervalIndex][0] = start;
      }

      // Change stop
      if (stop !== undefined) {
        newValue[intervalIndex][1] = stop;
      }
    }

    return newValue;
  }

  return day.concat([
    hasNoValues
      ? // Add
        [Math.min(DAY_LAST_MINUTE, day[day.length - 1]?.[1] || 0), DAY_LAST_MINUTE]
      : // Update
        [start!, stop!],
  ]);
}

interface IntervalDeleteProps {
  intervalIndex: number;
  onClick: (intervalIndex: number) => void;
}

const IntervalDelete: React.FC<IntervalDeleteProps> = memo(({ intervalIndex, onClick }) => {
  const onClickHandler = useCallback(() => {
    onClick(intervalIndex);
  }, [intervalIndex, onClick]);
  return (
    <IconButton aria-label="delete" onClick={onClickHandler} size="small">
      <DeleteIcon />
    </IconButton>
  );
});

export interface ScheduleDayProps {
  index: number;
  title: string;
  strings: Record<'fromLabel' | 'toLabel' | 'nextDay', string>;
  scheduleDay: ScheduleDayType;
  onChange: (index: number, newValue: ScheduleDayType) => void;
  onAddClick?: (index: number) => void;
}

export const ScheduleDay: React.FC<ScheduleDayProps> = memo(
  ({ title, index, scheduleDay, onChange, strings, onAddClick }) => {
    const update = useCallback(
      (start?: number, stop?: number, intervalIndex?: number) => {
        const newValue = updateScheduleDay(scheduleDay, start, stop, intervalIndex);
        onChange(index, newValue);
      },
      [index, onChange, scheduleDay],
    );

    const onStartChange = useCallback<TimePickerProps['onChange']>(
      (intervalIndex, value) => update(value, undefined, intervalIndex),
      [update],
    );

    const onStopChange = useCallback<TimePickerProps['onChange']>(
      (intervalIndex, value) => update(undefined, value, intervalIndex),
      [update],
    );
    const onAdd = useCallback(() => {
      if (onAddClick) {
        onAddClick(index);
      }
      update();
    }, [update, onAddClick, index]);

    const onDelete = useCallback(
      (intervalIndex: number) => update(undefined, undefined, intervalIndex),
      [update],
    );

    return (
      <ContainerCard>
        <Typography color="textSecondary">{title}</Typography>
        {scheduleDay &&
          scheduleDay.map(([start, stop], intervalIndex) => {
            const key = `${start}-${stop}-${intervalIndex}`;

            return (
              <Box key={key} display="flex" alignItems="flex-start" mt={2}>
                <Box mr={2}>
                  <TimePicker
                    index={intervalIndex}
                    label={strings.fromLabel}
                    value={start}
                    onChange={onStartChange}
                  />
                </Box>
                <Box mr={2}>
                  <TimePicker
                    index={intervalIndex}
                    label={strings.toLabel}
                    value={stop}
                    onChange={onStopChange}
                    helperText={start > stop ? strings.nextDay : ''}
                  />
                </Box>
                <Box mt={1}>
                  <IntervalDelete onClick={onDelete} intervalIndex={intervalIndex} />
                </Box>
              </Box>
            );
          })}
        <Box mt={2}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={onAdd}
          />
        </Box>
      </ContainerCard>
    );
  },
);
